/**
 * SpriteAnimRegistry — pure, framework-free registry of per-character
 * animation spritesheets (RD pixel-art sheets).
 *
 * manifest.json (public/assets/anims/) format:
 *   { "<charId>": { "<sheetState>": { file, fw, fh, frames, fps, loop } } }
 * where charId === the entity's assetId, and sheetState is one of the
 * AnimState names a sheet is authored for (idle | walk | attackH | hurt | ...).
 *
 * BootScene loads each sheet as a Phaser spritesheet under the key
 * `anim_<charId>_<sheetState>` and registers a matching global anim.
 * Entity asks `resolve(charId, animState)` for the best anim key to play,
 * falling back through related states so a character with only idle/walk/attack
 * still animates every AnimState without erroring.
 */
import type { AnimState } from '@/systems/AnimationStateMachine';

export interface SpriteAnimEntry {
  file: string;
  /** Frame width / height in px (square sheets: fw === fh). */
  fw: number;
  fh: number;
  /** Number of frames in the sheet (row-major). */
  frames: number;
  /** Playback frame rate. */
  fps: number;
  /** Loop forever (idle/walk) vs play-once (attack/hurt/ko). */
  loop: boolean;
}

export type SpriteAnimManifest = Record<string, Record<string, SpriteAnimEntry>>;

/** Build the Phaser texture/anim key for a character's sheet state. */
export function animKey(charId: string, sheetState: string): string {
  return `anim_${charId}_${sheetState}`;
}

/**
 * For a requested AnimState, the ordered list of sheet-states to try.
 * First one the character actually has wins; keeps every state animated.
 */
const FALLBACK: Record<AnimState, string[]> = {
  idle: ['idle'],
  walk: ['walk', 'idle'],
  run: ['run', 'walk', 'idle'],
  attackL: ['attackL', 'attackH', 'attack', 'idle'],
  attackH: ['attackH', 'attack', 'attackL', 'idle'],
  special: ['special', 'attackH', 'attack', 'idle'],
  throw: ['throw', 'attackH', 'attack', 'idle'],
  jump: ['jump', 'idle'],
  hurt: ['hurt', 'idle'],
  ko: ['ko', 'hurt', 'idle'],
  block: ['block', 'idle'],
  taunt: ['taunt', 'idle'],
};

export class SpriteAnimRegistry {
  private manifest: SpriteAnimManifest = {};

  /** Ingest the parsed anims manifest.json. */
  load(m: SpriteAnimManifest): void {
    this.manifest = m ?? {};
  }

  /** True when this character has at least one animation sheet. */
  hasChar(charId: string | undefined): boolean {
    return (
      !!charId &&
      this.manifest[charId] !== undefined &&
      Object.keys(this.manifest[charId]).length > 0
    );
  }

  /** Flattened list of every sheet for loader + anim registration. */
  entries(): { charId: string; sheetState: string; key: string; entry: SpriteAnimEntry }[] {
    const out: { charId: string; sheetState: string; key: string; entry: SpriteAnimEntry }[] = [];
    for (const charId of Object.keys(this.manifest)) {
      for (const sheetState of Object.keys(this.manifest[charId])) {
        out.push({
          charId,
          sheetState,
          key: animKey(charId, sheetState),
          entry: this.manifest[charId][sheetState],
        });
      }
    }
    return out;
  }

  /** Filtered entries for a specific set of charIds (used by deferred stage loading). */
  entriesForChars(
    charIds: ReadonlySet<string>,
  ): { charId: string; sheetState: string; key: string; entry: SpriteAnimEntry }[] {
    return this.entries().filter((e) => charIds.has(e.charId));
  }

  /** The sheet entry for a resolved (charId, sheetState), or undefined. */
  getEntry(charId: string, sheetState: string): SpriteAnimEntry | undefined {
    return this.manifest[charId]?.[sheetState];
  }

  /**
   * Best anim key to play for a requested AnimState, or null if the character
   * has no usable sheet. Resolves through the fallback chain.
   */
  resolve(charId: string | undefined, state: AnimState): string | null {
    if (!charId) return null;
    const char = this.manifest[charId];
    if (!char) return null;
    for (const s of FALLBACK[state] ?? [state]) {
      if (char[s]) return animKey(charId, s);
    }
    return null;
  }

  /** The sheet-state a requested AnimState resolves to (for entry lookup). */
  resolveSheetState(charId: string | undefined, state: AnimState): string | null {
    if (!charId) return null;
    const char = this.manifest[charId];
    if (!char) return null;
    for (const s of FALLBACK[state] ?? [state]) {
      if (char[s]) return s;
    }
    return null;
  }
}

/** Shared singleton (mirrors `spriteAtlas`). */
export const spriteAnims = new SpriteAnimRegistry();
